import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useApp, API, computeItemPrice, BAGS_PRICE_USD } from "../store";
import { Check, ZoomIn, Shield, Truck, RotateCw, Play } from "lucide-react";
import ProductCompare from "../components/ProductCompare";

const FULL_CARBON_KEY = "Full Carbon";

const isVideo = (src = "") => /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(src);

export default function ProductDetail() {
  const { slug } = useParams();
  const { t, addToCart, formatPrice, currency, rates, country, locale } = useApp();
  const [p, setP] = useState(null);
  const [opts, setOpts] = useState({});
  const [imgIdx, setImgIdx] = useState(0);
  const [added, setAdded] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [addons, setAddons] = useState([]);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => { if (!p) setLoadError(true); }, 6000);
    axios.get(`${API}/products/${slug}`).then((r) => {
      setP(r.data);
      clearTimeout(timer);
      setOpts({
        color: r.data.colors?.[0],
        size: r.data.sizes?.[0],
        battery: r.data.battery_options?.[0]?.label,
        wing: r.data.wing_options?.[0],
        stabilizer: r.data.stabilizer_options?.[0],
        carbon: false,
        bags: false,
      });
    }).catch((err) => {
      console.error("Product load failed:", err);
      setLoadError(true);
      clearTimeout(timer);
    });
    axios.get(`${API}/addons`).then((r) => setAddons(r.data)).catch(() => {});
    // Lightweight view tracking (admin metrics)
    axios.post(`${API}/track/product-view`, { slug, country }).catch(() => {});
    if (window.gtag) {
      window.gtag("event", "view_item", { item_id: slug });
    }
    return () => clearTimeout(timer);
  }, [slug, country]);

  if (loadError && !p) return (
    <main className="pt-40 pb-32 text-center bg-white">
      <div className="max-w-xl mx-auto px-6">
        <div className="overline mb-4 text-slate-400">Offline</div>
        <h1 className="font-serif-display text-4xl text-slate-900 mb-4">Produto indisponível</h1>
        <p className="text-slate-500 mb-8">Não foi possível carregar os detalhes deste produto agora. O servidor pode estar temporariamente offline.</p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/products/efoils" className="text-sm uppercase tracking-widest text-sky-500 hover:text-sky-700 font-ui">← Ver E-Foils</Link>
          <Link to="/products/jetboards" className="text-sm uppercase tracking-widest text-sky-500 hover:text-sky-700 font-ui">Ver JetBoards →</Link>
        </div>
      </div>
    </main>
  );

  if (!p) return <div className="pt-40 text-center text-slate-500">Loading…</div>;

  // Use admin-editable bags add-on price (with locale-aware label and BRL when set)
  const bagsAddon = addons.find((a) => a.id === "bags");
  const bagsPrice = bagsAddon ? Number(bagsAddon.price_usd) : 800;
  const bagsLabel = bagsAddon ? (locale === "pt" ? bagsAddon.label_pt : locale === "es" ? bagsAddon.label_es : bagsAddon.label) || bagsAddon.label : t.product.bags;

  // Recompute price with dynamic bags price
  const computeWithDynamicBags = () => {
    let pr = computeItemPrice(p, { ...opts, bags: false });
    if (opts.bags) pr += bagsPrice;
    return pr;
  };
  const price = computeWithDynamicBags();

  // Build "colors" list including Full Carbon as a color choice (if upcharge available)
  const colorOptions = [...(p.colors || [])];
  if (p.carbon_upcharge > 0 && !colorOptions.includes(FULL_CARBON_KEY)) {
    colorOptions.push(FULL_CARBON_KEY);
  }

  const selectColor = (c) => {
    if (c === FULL_CARBON_KEY) {
      setOpts({ ...opts, color: c, carbon: true });
    } else {
      setOpts({ ...opts, color: c, carbon: false });
    }
  };

  const displayPrice = currency === "BRL"
    ? ((p.price_brl_override ? Number(p.price_brl_override) + (price - Number(p.base_price)) * rates.USD_TO_BRL : price * rates.USD_TO_BRL))
        .toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })
    : price.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  const handleAdd = () => {
    addToCart({ product_id: p.id, slug: p.slug, name: p.name, image: p.images?.[0], quantity: 1, options: opts, unit_price: price });
    setAdded(true); setTimeout(() => setAdded(false), 1800);
    if (window.gtag) {
      window.gtag("event", "add_to_cart", { item_id: p.slug, value: price, currency: currency === "BRL" ? "BRL" : "USD" });
    }
  };

  const media = p.images || [];
  const current = media[imgIdx];
  const currentIsVideo = isVideo(current);

  return (
    <main className="pt-24 pb-32 bg-white" data-testid="product-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        {/* Gallery */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="relative aspect-[4/5] overflow-hidden bg-slate-50 group rounded-md" data-testid="product-gallery"
            onMouseEnter={() => setZoom(true)} onMouseLeave={() => setZoom(false)}>
            {currentIsVideo ? (
              <video src={current} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" data-testid="gallery-video" />
            ) : (
              <img src={current} alt={p.name} className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ${zoom ? "scale-125" : "scale-100"}`} />
            )}
            {country === "BR" && (
              <div className="absolute top-4 right-4 spec-pill bg-emerald-500/95 text-white border-0 backdrop-blur-sm" data-testid="pd-br-badge">
                🇧🇷 {t.product.brDelivery}
              </div>
            )}
            {!currentIsVideo && <div className="absolute top-4 left-4 spec-pill bg-white/90 backdrop-blur"><RotateCw size={12} /> 360° · <ZoomIn size={12} /> Zoom</div>}
          </div>
          <div className="grid grid-cols-4 gap-2 mt-3">
            {media.map((src, i) => {
              const v = isVideo(src);
              return (
                <button key={i} onClick={() => setImgIdx(i)} className={`relative aspect-square overflow-hidden border-2 rounded ${imgIdx === i ? "border-sky-500" : "border-slate-200 hover:border-slate-400"}`} data-testid={`thumb-${i}`}>
                  {v ? (
                    <>
                      <video src={src} muted className="w-full h-full object-cover" preload="metadata" />
                      <div className="absolute inset-0 grid place-items-center bg-black/30">
                        <Play size={18} className="text-white" fill="white" />
                      </div>
                    </>
                  ) : (
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Configurator */}
        <div>
          <Link to={`/products/${p.category}`} className="inline-block text-xs uppercase tracking-[0.2em] font-ui text-slate-500 hover:text-sky-500">← {p.category}</Link>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-none mt-3 text-slate-900">{p.name}</h1>
          <div className="text-slate-600 text-lg mt-3 italic font-serif-display">{p.tagline}</div>
          <div className="mt-5"><span className="spec-pill">{t.product.from} {formatPrice(p)}</span></div>

          <div className="mt-10 space-y-7 font-ui">
            {colorOptions.length > 0 && <Group label={t.product.color}>
              {colorOptions.map((c) => (
                <Pill key={c} sel={opts.color === c} onClick={() => selectColor(c)} testid={`color-${c}`}>
                  {c === FULL_CARBON_KEY ? `${t.product.carbon} (+${formatPrice(p.carbon_upcharge)})` : c}
                </Pill>
              ))}
            </Group>}

            {p.sizes?.length > 0 && <Group label={t.product.size}>
              {p.sizes.map((s) => <Pill key={s} sel={opts.size === s} onClick={() => setOpts({ ...opts, size: s })} testid={`size-${s}`}>{s}</Pill>)}
            </Group>}

            {p.battery_options?.length > 0 && (
              <div>
                <div className="overline mb-3">{t.product.battery}</div>
                <div className="grid grid-cols-3 gap-2">
                  {p.battery_options.map((b) => (
                    <button key={b.label} onClick={() => setOpts({ ...opts, battery: b.label })}
                      className={`py-4 px-3 border-2 rounded text-center transition-all ${opts.battery === b.label ? "border-sky-500 bg-sky-50 text-sky-600" : "border-slate-200 text-slate-700 hover:border-slate-400"}`}
                      data-testid={`battery-${b.label}`}>
                      <div className="text-xs uppercase tracking-widest font-semibold">{b.label}</div>
                      <div className="text-[11px] text-slate-500 mt-1">{b.price_delta > 0 ? `+${formatPrice(b.price_delta)}` : "Included"}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {p.wing_options?.length > 0 && <Group label={t.product.wing}>
              {p.wing_options.map((w) => <Pill key={w} sel={opts.wing === w} onClick={() => setOpts({ ...opts, wing: w })} testid={`wing-${w}`}>{w}</Pill>)}
            </Group>}

            {p.stabilizer_options?.length > 0 && <Group label={t.product.stabilizer}>
              {p.stabilizer_options.map((s) => <Pill key={s} sel={opts.stabilizer === s} onClick={() => setOpts({ ...opts, stabilizer: s })} testid={`stab-${s}`}>{s}</Pill>)}
            </Group>}

            {/* Extras: dynamic add-ons (e.g. Board & Battery Bags) */}
            {addons.length > 0 && (
              <div>
                <div className="overline mb-3">{t.product.extras}</div>
                {addons.map((ad) => (
                  <label key={ad.id} className="flex items-center justify-between border-2 border-slate-200 px-5 py-4 rounded-md cursor-pointer hover:border-sky-300 transition-colors mb-2" data-testid={`addon-toggle-${ad.id}`}>
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">{locale === "pt" ? ad.label_pt || ad.label : locale === "es" ? ad.label_es || ad.label : ad.label}</div>
                      <div className="text-slate-500 text-xs mt-1">+{currency === "BRL" ? `R$ ${Number(ad.price_brl).toLocaleString("pt-BR")}` : `$${Number(ad.price_usd).toLocaleString("en-US")}`}</div>
                    </div>
                    <input type="checkbox" checked={!!opts[ad.id]} onChange={(e) => setOpts({ ...opts, [ad.id]: e.target.checked })} className="w-5 h-5 accent-sky-500" />
                  </label>
                ))}
              </div>
            )}

            {/* Total + actions */}
            <div className="border-t border-slate-200 pt-7 flex items-end justify-between">
              <div>
                <div className="overline mb-2">{t.product.total}</div>
                <div className="font-serif-display text-5xl text-sky-600" data-testid="total-price">{displayPrice}</div>
              </div>
            </div>

            <button onClick={handleAdd} className="btn-primary w-full !justify-center !py-5 text-sm" data-testid="add-to-cart-btn">
              {added ? <><Check size={14} /> Added</> : t.product.addToCart}
            </button>

            <div className="grid grid-cols-3 gap-3 pt-2 text-xs text-slate-600">
              <div className="flex items-center gap-2"><Shield size={14} className="text-sky-500" /> 2-year warranty</div>
              <div className="flex items-center gap-2"><Truck size={14} className="text-sky-500" /> Free shipping</div>
              <div className="flex items-center gap-2"><RotateCw size={14} className="text-sky-500" /> 30-day returns</div>
            </div>
          </div>

          {/* Specs */}
          {Object.keys(p.specs || {}).length > 0 && (
            <div className="mt-16 border-t border-slate-200 pt-10">
              <div className="overline mb-6">{t.product.specs}</div>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(p.specs).map(([k, v]) => (
                  <div key={k} className="bg-slate-50 border border-slate-200 p-5 rounded">
                    <div className="text-xs uppercase tracking-widest text-slate-500">{k.replace(/_/g, " ")}</div>
                    <div className="font-serif-display text-3xl mt-1 text-slate-900">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-12 text-slate-700 leading-relaxed">{p.description}</div>

          <ProductCompare currentProduct={p} />
        </div>
      </div>
    </main>
  );
}

function Group({ label, children }) {
  return (
    <div>
      <div className="overline mb-3">{label}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Pill({ sel, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 border-2 rounded-full text-xs uppercase tracking-widest font-semibold transition-all ${sel ? "border-sky-500 bg-sky-50 text-sky-600" : "border-slate-200 text-slate-700 hover:border-slate-400"}`}>
      {children}
    </button>
  );
}
